import Router from '../../routes';
// These render ahead of the route table, and the route table routes to them as well. Taking
// them from its element list leaves one declaration per page rather than an eager import beside a
// lazy one. Rollup gives each page its own chunk either way, so the page code is not what an eager
// import costs. What follows the eager path is whatever a page alone depends on, which then sits
// in the entry chunk that every signed-in user downloads to reach a screen almost none of them see.
import {
  EmailNotVerifiedPage,
  NoOrganizationPage,
  PendingOrgInvitationsPage,
  RegistrationPage,
  RelationalProofPage,
  RelationalProofSuccessPage,
  SignUpWizardPage,
  SignUpWizardSuccessPage,
} from '../../routes/elements';
import { useCurrentOrgContext } from 'src/contexts/OrgContext';
import { useEffect, useContext } from 'react';
import { useOrg, useOrgs } from '../../hooks/query/orgHooks';
import LoadingScreen from 'src/components/loading-screen/LoadingScreen';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router';
import { SettingsContext } from 'src/components/settings/utils';
import { useIntercom } from 'react-use-intercom';
import { useUser } from 'src/hooks/query/userHooks';
import SignUpWizardLayout from 'src/layouts/signUp/SignUpWizardLayout';
import { mixpanelTrack } from 'src/utils/analytics';
import { Mode } from 'src/@types/mode';
import { useUserRole } from 'src/hooks/useUserRole';
import useSessionTabSync from 'src/hooks/useSessionTabSync';
import * as Sentry from '@sentry/react';
import RelationalLayout from 'src/layouts/relational/RelationalLayout';
import { Navigate } from 'react-router-dom';
import { InviteStatus } from 'src/@types/userOrg';
import { setMixpanelOrg, setMixpanelUser } from 'src/utils/analytics';

const getLegacySignUpRedirectPath = (locationPath: string) => {
  const [, route, discountOrg] = locationPath.split('/');

  if (route !== 'signup' && route !== 'sign-up') return null;
  if (!discountOrg) return '/get-started';
  if (discountOrg === 'success') return '/get-started/success';

  return `/get-started?parent_org=${encodeURIComponent(discountOrg)}`;
};

export default function OrganizationRouter() {
  const locationPath = useLocation().pathname;
  const { changeTheme } = useContext(SettingsContext);

  const { boot } = useIntercom();
  const { data: userRole, isPending: roleLoading } = useUserRole();

  const { orgId, setOrgId, setMode } = useCurrentOrgContext();

  // Mounted here rather than alongside the org state: applying a sibling tab's
  // switch moves the route and the palette too, and this is the first place
  // inside the router and below SettingsProvider.
  const awaitingHomeRoute = useSessionTabSync();

  const { isAuthenticated, loginWithRedirect, isLoading, user: auth0User } = useAuth0();

  if (
    !isLoading &&
    !isAuthenticated &&
    !locationPath.includes('sign-up') &&
    !locationPath.includes('signup') &&
    !locationPath.includes('get-started') &&
    !locationPath.includes('relational-proof')
  )
    loginWithRedirect();

  const {
    data: orgs,
    isSuccess: orgsLoaded,
    refetch: refetchOrgs,
  } = useOrgs({
    config: { enabled: isAuthenticated },
  });
  const { data: currentOrg } = useOrg();
  const { data: currentUser, status: currentUserStatus } = useUser({
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!currentUser) return;
    Sentry.setUser({
      email: currentUser.email,
    });
  }, [currentUser]);

  useEffect(() => {
    if (!orgsLoaded) return;

    const acceptedOrgs = orgs.filter(
      (org) => !org.invite_status || org.invite_status === InviteStatus.Accepted
    );

    if (acceptedOrgs.length === 0) {
      localStorage.removeItem('lastOrgId');
      localStorage.removeItem('lastMode');
      setOrgId('');
      return;
    }

    const lastOrgId = localStorage.getItem('lastOrgId');
    const lastMode = localStorage.getItem('lastMode');
    const isLastOrgAccepted = acceptedOrgs.some((userOrg) => userOrg.id === lastOrgId);
    if (lastOrgId && lastMode && isLastOrgAccepted) {
      setOrgId(lastOrgId);
      setMode(lastMode as Mode);
      changeTheme((lastMode as Mode) === 'dt' ? 'datatrust' : 'default');
    } else {
      setOrgId(acceptedOrgs[0].id);
      setMode(acceptedOrgs[0].managed_by_datatrust ? 'dt' : 'numinar');
      changeTheme(acceptedOrgs[0].managed_by_datatrust ? 'datatrust' : 'default');
      localStorage.setItem('lastOrgId', acceptedOrgs[0].id);
      localStorage.setItem('lastMode', acceptedOrgs[0].managed_by_datatrust ? 'dt' : 'numinar');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsLoaded]);

  useEffect(() => {
    const lastMode = localStorage.getItem('lastMode');
    changeTheme((lastMode as Mode) === 'dt' ? 'datatrust' : 'default');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orgId && currentUser && currentOrg) {
      setMode(currentOrg.managed_by_datatrust ? 'dt' : 'numinar');
      changeTheme(currentOrg.managed_by_datatrust ? 'datatrust' : 'default');

      if (!roleLoading && userRole && !currentOrg.managed_by_datatrust) {
        boot({
          email: currentUser.email,
          name: currentUser.first_name,
          userId: currentUser.id,
          userHash: currentUser.hmac,
          companies: [
            {
              companyId: currentOrg.id || '',
              name: currentOrg.name,
            },
          ],
          customAttributes: {
            role: userRole,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, currentUser, currentOrg, userRole, roleLoading]);

  useEffect(() => {
    if (currentUserStatus != 'success') return;
    const lastUserId = localStorage.getItem('lastUserId');
    if (currentUser && currentUser.id != lastUserId) {
      mixpanelTrack('Logged In');
      localStorage.setItem('lastUserId', currentUser.id);
    }
    // This event will never be sent to Data Trust because currentOrg is
    // still undefined at this point. I call setMixpanelUser again below to
    // remediate.
    setMixpanelUser(currentUser, userRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserStatus]);

  useEffect(() => {
    if (!currentOrg) return;
    if (currentUser && currentOrg.managed_by_datatrust) {
      // This will be called whenever someone switches into a DT org. I
      // don't think this is a problem though.
      setMixpanelUser(currentUser, userRole, currentOrg);
    }

    setMixpanelOrg(currentOrg);
  }, [currentOrg, currentUser, userRole]);

  const legacySignUpRedirectPath = getLegacySignUpRedirectPath(locationPath);
  const orgsList = orgs ?? [];
  const hasAcceptedOrg = orgsList.some(
    (org) => !org.invite_status || org.invite_status === InviteStatus.Accepted
  );

  if (legacySignUpRedirectPath) {
    return <Navigate to={legacySignUpRedirectPath} replace />;
  }

  if (locationPath === '/get-started/success')
    return (
      <SignUpWizardLayout>
        <SignUpWizardSuccessPage />
      </SignUpWizardLayout>
    );
  if (locationPath.includes('get-started'))
    return (
      <SignUpWizardLayout>
        <SignUpWizardPage />
      </SignUpWizardLayout>
    );
  if (locationPath === '/relational-proof/success') {
    return (
      <RelationalLayout>
        <RelationalProofSuccessPage />
      </RelationalLayout>
    );
  }

  if (locationPath === '/relational-proof') {
    return (
      <RelationalLayout>
        <RelationalProofPage />
      </RelationalLayout>
    );
  }

  if ((!orgId && !orgsLoaded) || currentUserStatus === 'pending' || !auth0User) {
    return <LoadingScreen />;
  } else if (!auth0User.email_verified) {
    if (!auth0User.email) {
      throw new Error(
        'There is no email associated with this auth0 account or it could not be retreived'
      );
    }
    return <EmailNotVerifiedPage email={auth0User.email} />;
  } else if (currentUserStatus === 'success' && !currentUser?.has_registered) {
    return <RegistrationPage />;
  } else if (!orgsList.length && orgsLoaded) {
    return <NoOrganizationPage />;
  } else if (!hasAcceptedOrg && orgsLoaded) {
    return (
      <PendingOrgInvitationsPage orgs={orgsList} onInviteStatusUpdated={() => refetchOrgs()} />
    );
  }

  // Two reasons to hold the org-scoped tree back, both about the key that
  // follows. Every preceding branch has settled that the user holds an accepted
  // org, so the effect that reads lastOrgId is about to set orgId, and waiting
  // keeps the key from changing once on boot and remounting every page's
  // queries. A sibling tab's switch sets orgId before its navigation lands, and
  // a child's effects flush before this one's, so mounting now would let the
  // previous org's route fetch under the new org's x-org-id header.
  if (!orgId || awaitingHomeRoute) {
    return <LoadingScreen />;
  }

  // Keyed on the org so a switch unmounts everything scoped to the org being
  // left. Pages hold query results in their own state, which no cache clear
  // reaches, so without this the previous org's rows stay on screen and
  // clickable under the new org's x-org-id header.
  return <Router key={orgId} />;
}
